import { useEffect, useState } from 'react'
import { logError } from '@/utils/errorUtils'
import { useMedplum } from '@medplum/react'
import { Modal, Button, FileInput, TextInput, Group, Box, Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconUpload } from '@tabler/icons-react'
import { Patient, Practitioner } from '@medplum/fhirtypes'
import { IconX } from '@tabler/icons-react'
import { Dropzone, MIME_TYPES } from '@mantine/dropzone'
import { DatePickerInput } from '@mantine/dates'
import dayjs from 'dayjs'
import { formatHumanName } from '@medplum/core'
import { SNOMED_USER_ROLES } from '@/utils'
import { uplaoadDiagnosticReport, uploadDocument, uploadOrderDocument } from './service'
import { showNotification } from '@mantine/notifications'
import { DeliveryMethodToggle, DeliveryMethod } from './components/DeliveryMethodToggle'

export const UploadDocumentModal = ({
  isOpen,
  onClose,
  patient,
  successCallback,
}: {
  isOpen: boolean
  onClose: () => void
  patient?: Patient
  successCallback?: (createdReference: string) => void
}) => {
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [providerOptions, setProviderOptions] = useState<{ value: string; label: string }[]>([])
  const [providerSearch, setProviderSearch] = useState('')
  const medplum = useMedplum()
  const profile = medplum.getProfile()

  useEffect(() => {
    if (isOpen) {
      form.setFieldValue('date', dayjs().format('YYYY-MM-DD'))
    }
    if (profile?.resourceType === 'Practitioner') {
      setPractitioner(profile)
      form.setFieldValue('reviewedBy', formatHumanName(profile.name?.[0]) || '')
      form.setFieldValue('providerName', formatHumanName(profile.name?.[0]) || '')
      form.setFieldValue('providerReference', `Practitioner/${profile.id}`)
    }
  }, [isOpen, patient])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // The signed-in practitioner is the default, so keep them selectable whatever their role —
    // and before the search resolves, so a failed load still leaves the default to pick.
    const signedIn =
      profile?.resourceType === 'Practitioner'
        ? [
            {
              value: `Practitioner/${profile.id}`,
              label: formatHumanName(profile.name?.[0]) || profile.id || '',
            },
          ]
        : []
    setProviderOptions(signedIn)
    medplum
      .searchResources('Practitioner', {
        '_has:PractitionerRole:practitioner:role': SNOMED_USER_ROLES.MEDICAL_DOCTOR.code,
        _count: '1000',
      })
      .then((practitioners) => {
        const options = practitioners
          .map((item) => ({
            value: `Practitioner/${item.id}`,
            label: formatHumanName(item.name?.[0]) || item.id || '',
          }))
          .filter((option) => !signedIn.some((own) => own.value === option.value))
        setProviderOptions([...signedIn, ...options])
      })
      .catch((e) => logError('patient', 'Error loading providers', e))
  }, [isOpen, medplum, profile])

  const closeModal = () => {
    form.reset()
    onClose()
  }

  const form = useForm({
    initialValues: {
      title: '',
      file: null as File | null,
      documentType: 'DiagnosticReport' as string,
      reviewedBy: '' as string,
      // Mantine 8's DatePickerInput yields a calendar day as 'YYYY-MM-DD'; never parse it with `new Date` (UTC midnight → previous day west of UTC)
      date: null as string | null,
      organizationName: '' as string | undefined,
      diagnosticReportType: '' as string | undefined,
      providerName: '' as string | undefined,
      providerReference: '' as string | undefined,
      labReferenceId: '' as string | undefined,
      orderDeliveryMethod: undefined as DeliveryMethod,
      faxNumber: '' as string | undefined,
    },
    validate: {
      title: (value) => (value.trim().length < 3 ? 'Title cannot be empty' : null),
      file: (file) => (!file ? 'Please select a file' : null),
      documentType: (value) => (!value ? 'Please select document type' : null),
      reviewedBy: (value) => (!value ? 'Reviewed by is required' : null),
      date: (value) => {
        if (!value) {
          return 'Date is required'
        }
        if (value > dayjs().format('YYYY-MM-DD')) {
          return 'Date cannot be in the future'
        }
        return null
      },
      organizationName: (value, values) => {
        if (values.documentType !== 'DiagnosticReport') {
          return null
        }
        if (!value || value.trim().length < 3) {
          return 'Organization name is required for Labs/Imaging'
        }
        return null
      },
      providerName: (value, values) => {
        if (values.documentType !== 'Order') {
          return null
        }
        if (!value || value.trim().length < 3) {
          return 'Provider name is required for Order documents'
        }
        return null
      },
      faxNumber: (value, values) => {
        if (values.documentType !== 'Order' || values.orderDeliveryMethod !== 'fax') {
          return null
        }
        if (!value || value.trim().length < 8) {
          return 'Please provide a valid fax number'
        }
        return null
      },
    },
  })

  const setFileAndUrl = (file: File | null) => {
    if (!file) {
      form.setFieldValue('file', null)
      return
    }

    // allow only PDF or TIFF (double-check by MIME or extension)
    const allowedTypes = new Set([
      'application/pdf',
      'image/tiff',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/zip',
      'application/octet-stream', // some browsers use generic when dropping local files
    ])

    if (!allowedTypes.has(file.type)) {
      form.setFieldError('file', 'Only PDF or tiff files are allowed')
      form.setFieldValue('file', null)
      return
    }

    form.clearFieldError('file')
    form.setFieldValue('file', file)
  }
  const handleSuccessDocumentUpload = (createdReference?: string) => {
    form.reset()
    closeModal()
    showNotification({
      color: 'green',
      title: 'Success',
      message: 'Document uploaded successfully',
    })
    if (createdReference) {
      successCallback?.(createdReference)
    }
  }

  const onSubmit = async () => {
    if (!practitioner || !patient || !form.values.file) {
      return
    }
    setIsUploading(true)
    try {
      const formattedDate = form.values.date
        ? dayjs(form.values.date).format('MM-DD-YYYY')
        : undefined

      let createdReference: string | undefined
      if (form.values.documentType === 'DiagnosticReport') {
        const { docRef } = await uplaoadDiagnosticReport({
          file: form.values.file,
          patient,
          title: form.values.title,
          createdAt: formattedDate,
          medplum,
          organizationName: form.values.organizationName || '',
        })
        createdReference = `DiagnosticReport/${docRef.id}`
      } else if (form.values.documentType === 'Order') {
        const { requestGroup } = await uploadOrderDocument({
          file: form.values.file,
          formValues: form.values,
          patient,
          practitioner,
          title: form.values.title,
          createdAt: formattedDate,
          medplum,
        })
        createdReference = `RequestGroup/${requestGroup.id}`
      } else {
        const { docRef } = await uploadDocument({
          file: form.values.file,
          practitioner,
          patient,
          title: form.values.title,
          createdAt: formattedDate,
          medplum,
          docType: form.values.documentType || 'others',
        })
        createdReference = `DocumentReference/${docRef.id}`
      }
      handleSuccessDocumentUpload(createdReference)
    } catch (e) {
      logError('patient', 'Error uploading document', e)
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Error uploading document',
      })
    } finally {
      setIsUploading(false)
    }
  }
  return (
    <Modal
      className="addpatient-modal"
      opened={isOpen}
      onClose={closeModal}
      centered
      withCloseButton={false}
      classNames={{
        content: 'rounded-md shadow-lg !p-2',
        body: '!p-0',
      }}
      radius="md"
      size="450px"
    >
      <Box className="p-4">
        <div className="mb-4 flex w-full items-center justify-between">
          <span className="w-6" />
          <h2 className="flex-1 text-center text-xl font-semibold">Add document</h2>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            className="!min-w-0 !border-0 !p-2 hover:bg-gray-200"
            onClick={onClose}
            aria-label="Close"
          >
            <IconX size={20} />
          </Button>
        </div>
        <form
          onSubmit={form.onSubmit(onSubmit)}
          className="mx-auto flex w-full max-w-md flex-col gap-2"
        >
          <Select
            label="Document Type"
            placeholder="Select document type"
            allowDeselect={false}
            data={[
              { value: 'progressNote', label: 'Progress Note' },
              { value: 'HistoryPhysical', label: 'History and Physical' },
              { value: 'DiagnosticReport', label: 'Labs/Imaging' },
              { value: 'Order', label: 'Order' },
              { value: 'Others', label: 'Document' },
            ]}
            defaultValue={'DiagnosticReport'}
            {...form.getInputProps('documentType')}
          />
          <TextInput
            disabled
            classNames={{ input: '!text-[#000]' }}
            value={formatHumanName(practitioner?.name?.[0]) || ''}
            variant="outlined"
            label="Reviewed by"
            withAsterisk
            error={form.errors.reviewedBy}
            {...form.getInputProps('reviewedBy')}
          />
          {form.values.documentType === 'Order' && (
            <>
              <Select
                label="Provider name"
                placeholder="Select provider"
                withAsterisk
                searchable
                clearable
                data={providerOptions}
                value={form.values.providerReference || null}
                searchValue={providerSearch}
                onSearchChange={setProviderSearch}
                // Focusing clears the query so a click on the field starts a new search; blur
                // restores the selected name.
                onFocus={() => setProviderSearch('')}
                onBlur={() => setProviderSearch(form.values.providerName || '')}
                nothingFoundMessage="No providers found"
                error={form.errors.providerName}
                onChange={(value, option) => {
                  form.setFieldValue('providerName', value ? option.label : '')
                  form.setFieldValue('providerReference', value ?? '')
                }}
              />
              <TextInput
                label={'Lab Reference ID (optional)'}
                placeholder="Enter lab Reference ID"
                {...form.getInputProps('labReferenceId')}
              />
              <DeliveryMethodToggle
                value={form.values.orderDeliveryMethod}
                onChange={(value) => form.setFieldValue('orderDeliveryMethod', value)}
                disabled={isUploading}
                inputProps={{ ...form.getInputProps('faxNumber') }}
              />
            </>
          )}

          <TextInput
            label={
              form.values.documentType === 'DiagnosticReport'
                ? 'Type of Diagnostic Report'
                : 'Document name'
            }
            placeholder="Enter title"
            withAsterisk
            error={form.errors.title}
            {...form.getInputProps('title')}
          />
          {form.values.documentType === 'DiagnosticReport' && (
            <TextInput
              label="Organization Name"
              placeholder="Enter organization name"
              withAsterisk
              error={form.errors.organizationName}
              {...form.getInputProps('organizationName')}
            />
          )}
          <DatePickerInput
            label="Document date"
            name="date"
            withAsterisk
            value={form.values.date}
            onChange={(value) => form.setFieldValue('date', value)}
            maxDate={new Date()}
            error={form.errors.date}
            placeholder="Select document date"
            valueFormat="MM/DD/YYYY"
            classNames={{
              label: 'line-[1.5] text-[#7D7987] font-[13px]',
              input: 'h-[36px] w-[250px]',
            }}
          />
          <FileInput
            label="File"
            placeholder="Choose PDF, TIFF, image, or ZIP files"
            leftSection={<IconUpload size={16} />}
            accept=".pdf,.tiff,.jpg,.jpeg,.png,.zip"
            value={form.values.file}
            onChange={setFileAndUrl}
            error={form.errors.file}
            withAsterisk
          />

          <Dropzone
            multiple={false}
            onDrop={(files) => setFileAndUrl(files[0] ?? null)}
            onReject={() =>
              form.setFieldError('file', 'Only PDF, TIFF, image, or ZIP files are allowed')
            }
            className="mt-4 rounded-xl border border-dashed border-slate-300 p-6"
            accept={{
              'image/jpeg': ['.jpg', '.jpeg'],
              [MIME_TYPES.pdf]: ['.pdf'],
              'image/tiff': ['.tiff', '.tif'],
              'image/png': ['.png'],
              'application/zip': ['.zip'],
            }}
          >
            <div className="flex items-center justify-center gap-3 text-slate-600">
              <IconUpload size={18} />
              <span>Drop PDF, TIFF, image, or ZIP file here or click to select file</span>
            </div>
          </Dropzone>

          <Group mt="md" className="flex justify-end">
            <Button type="submit" className="px-5" disabled={isUploading}>
              <span>Upload file</span>
            </Button>
            <Button
              variant="default"
              className="px-5"
              onClick={() => {
                form.reset()
                closeModal()
              }}
            >
              Cancel
            </Button>
          </Group>
        </form>
      </Box>
    </Modal>
  )
}

export default UploadDocumentModal
